import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import type { MeasureUnit } from "@/lib/measurementConverter";

const STORAGE_KEY = "preferred_measure_unit";
const STORAGE_KEY_USER_SET = "preferred_measure_unit_user_set"; // marks explicit choice

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

/**
 * Default measurement unit for a given market.
 * US users expect cups/oz/tsp/tbsp; everyone else gets metric.
 */
function defaultUnitForMarket(market: string): MeasureUnit {
  return market === "us" ? "cups" : "metric";
}

/**
 * Hook to persist the user's preferred measurement unit.
 * - localStorage for guests, DB for logged-in users.
 * - First-time auto-default: US market → "cups", everywhere else → "metric".
 * - Once the user picks a unit explicitly, the auto-default never overrides it.
 */
export function usePreferredMeasureUnit() {
  const { user } = useAuth();
  const { market } = useLocale();
  const [unit, setUnitState] = useState<MeasureUnit>(() => {
    const stored = safeGet(STORAGE_KEY);
    if (stored === "metric" || stored === "cups" || stored === "ounces") {
      return stored as MeasureUnit;
    }
    // No stored value yet — fall back to market-based default
    return defaultUnitForMarket(market);
  });
  const [loaded, setLoaded] = useState(false);

  // Load from DB on login
  useEffect(() => {
    if (!user?.id) {
      setLoaded(true);
      return;
    }
    supabase
      .from("app_users")
      .select("preferred_measure_unit")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.preferred_measure_unit) {
          const dbUnit = data.preferred_measure_unit as MeasureUnit;
          setUnitState(dbUnit);
          safeSet(STORAGE_KEY, dbUnit);
          safeSet(STORAGE_KEY_USER_SET, "true");
        }
        setLoaded(true);
      });
  }, [user?.id]);

  // Auto-apply market default the FIRST time only (no manual override yet).
  // E.g. user buys Clean Sweets (market becomes US) and never chose units → switch to cups.
  useEffect(() => {
    if (!loaded) return;
    if (safeGet(STORAGE_KEY_USER_SET) === "true") return;
    if (safeGet(STORAGE_KEY)) return; // already auto-applied earlier
    const auto = defaultUnitForMarket(market);
    setUnitState(auto);
    safeSet(STORAGE_KEY, auto);
    if (user?.id) {
      supabase
        .from("app_users")
        .update({ preferred_measure_unit: auto } as any)
        .eq("id", user.id)
        .then(() => {});
    }
  }, [market, loaded, user?.id]);

  const setUnit = useCallback(
    (newUnit: MeasureUnit) => {
      setUnitState(newUnit);
      safeSet(STORAGE_KEY, newUnit);
      safeSet(STORAGE_KEY_USER_SET, "true");
      if (user?.id) {
        supabase
          .from("app_users")
          .update({ preferred_measure_unit: newUnit } as any)
          .eq("id", user.id)
          .then(() => {});
      }
    },
    [user?.id],
  );

  return { unit, setUnit, loaded };
}
